import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { ADMISSIONS_FAQS } from '@/data/admissions';
import { cn } from '@ibas/ui';

/**
 * AdmissionsFaq (Admissions · Section 5) — the FAQ as a hand-built, fully
 * accessible single-open accordion. There is no Accordion in @ibas/ui, so this
 * composes native <button> headers (aria-expanded / aria-controls) against
 * controlled answer regions; only one panel is open at a time (openIndex state).
 *
 * Motion: a two-column reveal (sticky intro on lg, accordion on the right), a
 * chevron that rotates 180° on open, and a Framer Motion height-auto expand via
 * AnimatePresence. When reduced motion is requested the open panel renders with
 * no animation so content is never stranded hidden. Every FAQ string is
 * reproduced verbatim from ADMISSIONS_FAQS — no item is added or altered.
 */
export function AdmissionsFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const reduced = useReducedMotion();

  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
        {/* ── LEFT: sticky intro ─────────────────────────────────────────── */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Frequently asked questions
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Answers before you apply.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              The essentials on intakes, study modes, accreditation and fees — the details our
              applicants ask about most.
            </p>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              Still have a question? Email{' '}
              <a
                href="mailto:admissions@ibas.edu.eu"
                data-cursor
                className="font-medium text-brand-gold underline decoration-brand-gold/50 underline-offset-4 transition-colors hover:decoration-brand-gold focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold motion-reduce:transition-none"
              >
                admissions@ibas.edu.eu
              </a>
              .
            </p>
          </ScrollReveal>
        </div>

        {/* ── RIGHT: single-open accordion ───────────────────────────────── */}
        <ScrollReveal
          from="up"
          stagger={0.06}
          className="divide-y divide-border border-y border-border"
        >
          {ADMISSIONS_FAQS.map((faq, index) => {
            const isOpen = openIndex === index;
            const panelId = `admissions-faq-panel-${index}`;
            const buttonId = `admissions-faq-button-${index}`;

            const answer = (
              <p className="max-w-2xl pb-5 text-base leading-relaxed text-muted-foreground">
                {faq.answer}
              </p>
            );

            return (
              <div key={faq.question}>
                <h3>
                  <button
                    type="button"
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className={cn(
                      'flex w-full items-center justify-between gap-4 py-5 text-left',
                      'transition-colors hover:text-brand-navy focus-visible:rounded-sm focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-gold motion-reduce:transition-none',
                    )}
                  >
                    <span className="font-display text-lg leading-snug font-semibold text-foreground md:text-xl">
                      {faq.question}
                    </span>
                    <ChevronDown
                      aria-hidden
                      className={cn(
                        'size-5 shrink-0 text-brand-gold transition-transform duration-300 motion-reduce:transition-none',
                        isOpen && 'rotate-180',
                      )}
                    />
                  </button>
                </h3>

                {reduced ? (
                  isOpen && (
                    <div id={panelId} role="region" aria-labelledby={buttonId}>
                      {answer}
                    </div>
                  )
                ) : (
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="panel"
                        id={panelId}
                        role="region"
                        aria-labelledby={buttonId}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          height: { duration: 0.36, ease: [0.22, 1, 0.36, 1] },
                          opacity: { duration: 0.25 },
                        }}
                        className="overflow-hidden"
                      >
                        {answer}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })}
        </ScrollReveal>
      </div>
    </section>
  );
}
